import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Loader from '@node-space/storybook-components/dist/Loader'
import { Box, Button, Callout, Text } from '@node-space/ui-components'
import { DateFormat, PathNames } from 'constants/General'
import { ContactCase } from 'types/contacts'
import { useContactCaseSidePanelHelper } from '../hooks/useContactCaseSidePanelHelper'
import { ChannelPaymentSidePanelBody } from './ChannelPaymentSidePanelBody'
import { DepositSidePanelBody } from './DepositSidePanelBody'
import { PaymentInSidePanelBody } from './PaymentInSidePanelBody'
import { SidePanelRow } from './SidePanelRow'

export type ContactCaseSidePanelBodyProps = {
  contactCase: ContactCase
}

export const ContactCaseSidePanelBody = ({ contactCase }: ContactCaseSidePanelBodyProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const paymentContextType = contactCase?.paymentContext?.type

  const { isLoading, isError, payment, channel, isChannelPayment, isPaymentIn, isDirectDeposit } =
    useContactCaseSidePanelHelper({
      contactCase,
      paymentContextType,
    })

  const deepLinkToOriginalPayments = (reference: string) => {
    if (isChannelPayment) {
      navigate(`${PathNames.PAYMENTS}${PathNames.CHANNEL}?dlt-reference=${reference}`)
    } else {
      navigate(`${PathNames.PAYMENTS}${PathNames.CRYPTO}?dlt-reference=${reference}`)
    }
  }

  return (
    <>
      {isError && !isLoading && (
        <Callout variant="error">{t('somethingWentWrongFetchTransaction')}</Callout>
      )}

      {isLoading && !isError && (
        <Box flex justify="center">
          <Loader theme="dark" size="small" />
        </Box>
      )}

      {!isLoading && !isError && (
        <Box flexCol gapY={12}>
          <Box>
            <Text variant="heading-4" color="prominent">
              {t('transactionDetails')}
            </Text>
          </Box>
          {isDirectDeposit && <DepositSidePanelBody contactCase={contactCase} />}

          {isPaymentIn && <PaymentInSidePanelBody payment={payment} contactCase={contactCase} />}
          {isChannelPayment && (
            <ChannelPaymentSidePanelBody channel={channel} contactCase={contactCase} />
          )}

          <SidePanelRow
            label={t('date')}
            content={`${dayjs(contactCase?.paymentCreated).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}`}
          />

          {(payment?.reference || channel?.reference) && (
            <Box flex justify="center" paddingY={12}>
              <Text variant="body-text">
                <Button
                  onClick={() =>
                    deepLinkToOriginalPayments(payment?.reference || channel?.reference)
                  }
                  variant="ghost"
                  noPadding
                >
                  {t('wallets.searchTransaction.dltDeepLinkText')}
                </Button>
              </Text>
            </Box>
          )}
        </Box>
      )}
    </>
  )
}
